import { FieldArray } from "formik";
import React from "react";

import {
    Button,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete } from '@mui/material';
import { PopoverLink } from "../../../../common";
import { RULE_CRITERIA_DATE, RULE_CRITERIA_INT, RULE_CRITERIA_LONG, RULE_CRITERIA_STRING } from "../../../../store/constants/contract";
import { alphabets, dateEquators, FIELD_TYPE_DATE, integerEquators, StringEquators } from "../../../../store/constants/pages";

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    textField: {
        width: '85%',
        marginLeft: theme.spacing(1),
    },
    portlet: {
        'box-shadow': 'none',
    },
    formControl: {
        width: '85%',
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(1),
    },
    autoComplete: {
        width: '85%',
        marginLeft: theme.spacing(1),
    },
    button: {
        margin: theme.spacing(1)
    },
    paper: {
        marginTop: theme.spacing(2),
    },
    iconButton: {
        marginTop: theme.spacing(3),
    },
    clearButton: {
        marginTop: theme.spacing(3),
        marginLeft: '10px',
        color: 'rgba(0, 0, 0, 0.54)'
    },
    input: {
        marginTop: theme.spacing(4),
        //marginLeft: theme.spacing(2),
        width: '90%',
    },
    helpClass: {
        position: 'relative',
        top: 10,
        marginLeft: -12,
    },
}));

export default function ContractOverrideCriteriaFieldArray({
    overrideGroup, handleBlur, handleChange, touched, errors, contractOverrideCriteria, setFieldValue, npiRoles,
    disabled, facilities, groupIndex
}) {
    const classes = useStyles();

    function getHelperText(key, index) {
        if (errors.contractOverrideGroupXref && errors.contractOverrideGroupXref[groupIndex]) {
            if (errors.contractOverrideGroupXref[groupIndex].contractOverrideCriteriaXref) {
                if (errors.contractOverrideGroupXref[groupIndex].contractOverrideCriteriaXref[index]) {
                    return errors.contractOverrideGroupXref[groupIndex].contractOverrideCriteriaXref[index][key];
                }
                return false;
            }
        }
        return false;
    }

    function getError(key, index) {
        if (touched.contractOverrideGroupXref && touched.contractOverrideGroupXref[groupIndex]) {
            if (touched.contractOverrideGroupXref[groupIndex].contractOverrideCriteriaXref) {
                if (touched.contractOverrideGroupXref[groupIndex].contractOverrideCriteriaXref[index]) {
                    return touched.contractOverrideGroupXref[groupIndex].contractOverrideCriteriaXref[index][key];
                }
                return false;
            }
        }
        return false;
    }

    function handleCriteriaChangeHandler(index, criteriaObj) {
        setFieldValue(`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.contractOverrideCriteriaId`, criteriaObj);
        setFieldValue(`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.type`, criteriaObj?.fieldType); // criteria Type i.e. INT, DATE, STRING
        setFieldValue(`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.equator`, criteriaObj?.id === 5 ? 'equals' : '');
        setFieldValue(`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.value`, '');
    }

    function handleEquatorChangeHandler(e, index) {
        setFieldValue(`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.equator`, e.target.value);
        if (e.target.value === 'alpha_between' || e.target.value === 'in_between') {
            setFieldValue(`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.isBetween`, true);
        } else {
            setFieldValue(`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.isBetween`, false);
        }
    }

    function getEquators(fieldType) {
        switch (fieldType) {
            case RULE_CRITERIA_STRING:
                return StringEquators.equatorArr
            case RULE_CRITERIA_DATE:
                return dateEquators.equatorArr
            case RULE_CRITERIA_INT:
                return integerEquators.equatorArr
            case RULE_CRITERIA_LONG:
                return integerEquators.equatorArr
            default:
                return StringEquators.equatorArr
        }
    }

    function hoverColumn(text) {
        return (
            <PopoverLink
                columnData={<i className={`fas fa-question-circle text-info`}></i>}
                hoverData={[
                    {
                        data: text
                    }
                ]}
            />
        );
    }

    return (
        (<FieldArray
                name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref`}
                render={({ remove, insert }) => (
                    <Grid container key={`contractOverrideGroupXref.${groupIndex}.item`}>
                        <Grid item xs={12}>
                            {overrideGroup.contractOverrideCriteriaXref?.map((overrideCriteria, index) =>
                                <Grid container>
                                    <Grid item xs={4}>
                                        <Autocomplete
                                            className={classes.autoComplete}
                                            value={overrideCriteria.contractOverrideCriteriaId}
                                            disableClearable={false}
                                            onChange={(e, value) => { handleCriteriaChangeHandler(index, value) }}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            options={contractOverrideCriteria}
                                            getOptionLabel={(option) => option.name}
                                            renderInput={(params) =>
                                                <TextField {...params}
                                                    name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.contractOverrideCriteriaId`}
                                                    label="Criteria"
                                                    margin="normal" />
                                            }
                                        />
                                    </Grid>
                                    {(overrideCriteria.contractOverrideCriteriaId?.id !== 5 && overrideCriteria.contractOverrideCriteriaId?.id !== 6)  && <>
                                    <Grid item xs={3}>
                                        <FormControl className={classes.formControl}>
                                            <InputLabel htmlFor={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.equator`}>Equator</InputLabel>
                                            <Select
                                                required
                                                value={overrideCriteria?.equator}
                                                onChange={e => handleEquatorChangeHandler(e, index)}
                                                disabled={!overrideCriteria?.contractOverrideCriteriaId}
                                                input={<Input id={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.equator`} />}
                                                inputProps={{
                                                    name: `contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.equator`,
                                                    id: `contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.equator`,
                                                }}
                                                error={Boolean(getHelperText('equator', index) && getError('equator', index))}
                                            >
                                                {getEquators(overrideCriteria?.type).map(equator => (
                                                    <MenuItem
                                                        key={equator.value}
                                                        value={equator.value}
                                                    >
                                                        {equator.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText error>{getError('equator', index) ? getHelperText('equator', index) : ''}</FormHelperText>

                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={3}>
                                        {overrideCriteria?.equator === 'alpha_between' || overrideCriteria?.equator === 'in_between' ?
                                            <Grid container>
                                                {overrideCriteria?.type === 'int' ?
                                                    <>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                type='number'
                                                                name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.startsAt`}
                                                                className={classes.input}
                                                                placeholder="Starts at"
                                                                color="secondary"
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={overrideCriteria.startsAt}
                                                                helperText={Boolean(getError('startsAt', index)) ? getHelperText('startsAt', index) : ''}
                                                                error={Boolean(getHelperText('startsAt', index) && getError('startsAt', index))}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                type='number'
                                                                name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.endsAt`}
                                                                className={classes.input}
                                                                placeholder="Ends at"
                                                                color="secondary"
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={overrideCriteria.endsAt}
                                                                error={Boolean(getHelperText('endsAt', index) && getError('endsAt', index))}
                                                                helperText={Boolean(getError('endsAt', index)) ? getHelperText('endsAt', index) : ''}
                                                            />
                                                        </Grid>
                                                    </>
                                                    : overrideCriteria?.type === FIELD_TYPE_DATE ?
                                                        <>
                                                            <Grid item xs={6}>
                                                                <TextField
                                                                    type='date'
                                                                    name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.startsAt`}
                                                                    className={classes.input}
                                                                    margin="normal"
                                                                    placeholder="Starts at"
                                                                    color="secondary"
                                                                    onBlur={handleBlur}
                                                                    onChange={handleChange}
                                                                    value={overrideCriteria.startsAt}
                                                                    helperText={Boolean(getError('startsAt', index)) ? getHelperText('startsAt', index) : ''}
                                                                    error={Boolean(getHelperText('startsAt', index) && getError('startsAt', index))}
                                                                    InputLabelProps={{
                                                                        shrink: true,
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <TextField
                                                                    type='date'
                                                                    name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.endsAt`}
                                                                    className={classes.input}
                                                                    margin="normal"
                                                                    placeholder="Ends at"
                                                                    color="secondary"
                                                                    onBlur={handleBlur}
                                                                    onChange={handleChange}
                                                                    value={overrideCriteria.endsAt}
                                                                    helperText={Boolean(getError('endsAt', index)) ? getHelperText('endsAt', index): ''}
                                                                    error={Boolean(getHelperText('endsAt', index) && getError('endsAt', index))}
                                                                    InputLabelProps={{
                                                                        shrink: true,
                                                                    }}
                                                                />
                                                            </Grid>
                                                        </> :
                                                        <>
                                                            <Grid item xs={6}>
                                                                <FormControl className={classes.formControl}>
                                                                    <InputLabel htmlFor="startsAt">Starts At</InputLabel>
                                                                    <Select
                                                                        onBlur={handleBlur}
                                                                        onChange={handleChange}
                                                                        value={overrideCriteria.startsAt}
                                                                        error={Boolean(getHelperText('startsAt', index) && getError('startsAt', index))}
                                                                        inputProps={{
                                                                            name: `contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.startsAt`,
                                                                            id: "startsAt"
                                                                        }}
                                                                    >
                                                                        {alphabets.map((item, index) => (
                                                                            <MenuItem
                                                                                key={`${index}-${item}`}
                                                                                value={item.toLowerCase()}
                                                                            >
                                                                                {item}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                    <FormHelperText error>{getHelperText('startsAt', index) || ''}</FormHelperText>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <FormControl className={classes.formControl}>
                                                                    <InputLabel htmlFor="endsAt">Ends At</InputLabel>
                                                                    <Select
                                                                        onBlur={handleBlur}
                                                                        onChange={handleChange}
                                                                        value={overrideCriteria.endsAt}
                                                                        error={Boolean(getHelperText('endsAt', index) && getError('endsAt', index))}
                                                                        inputProps={{
                                                                            name: `contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.endsAt`,
                                                                            id: "endsAt"
                                                                        }}
                                                                    >
                                                                        {alphabets.map((item, index) => (
                                                                            <MenuItem
                                                                                key={`${index}-${item}`}
                                                                                value={item.toLowerCase()}
                                                                            >
                                                                                {item}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                    <FormHelperText error>{getHelperText('endsAt', index) || ''}</FormHelperText>
                                                                </FormControl>
                                                            </Grid>
                                                        </>
                                                }
                                            </Grid>
                                            :
                                            <TextField
                                                label="Value"
                                                name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.value`}
                                                className={classes.textField}
                                                margin="normal"
                                                disabled={overrideCriteria?.equator === ''}
                                                type={overrideCriteria?.type === RULE_CRITERIA_INT ? 'number' : overrideCriteria.type === RULE_CRITERIA_DATE ? 'date' : 'text'}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={overrideCriteria?.value}
                                                InputLabelProps={overrideCriteria.type === RULE_CRITERIA_DATE ?
                                                    {
                                                        shrink: true,
                                                    } : {

                                                    }
                                                }
                                                helperText={getError('value', index) ? getHelperText('value', index) : ''}
                                                error={Boolean(getHelperText('value', index) && getError('value', index))}

                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">
                                                        <span style={{pointerEvents: 'auto'}}>
                                                            {hoverColumn(overrideCriteria?.type === RULE_CRITERIA_INT ?
                                                                'Only single numeric value is allowed' :
                                                                'Add single string value or use comma separation for multiple overrideGroup'
                                                            )}
                                                        </span>
                                                    </InputAdornment>
                                                }}
                                            />
                                        }
                                    </Grid>
                                    </>
                                    }
                                    {overrideCriteria.contractOverrideCriteriaId?.id === 5 &&
                                    <Grid item xs={6}>
                                        <FormControl className={classes.formControl}>
                                            <InputLabel htmlFor="facility">Select Facility</InputLabel>
                                            <Select
                                                required
                                                value={overrideCriteria?.value}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.value`}
                                                //helperText={getHelperText('npiRoleId', index) && getError('npiRoleId', index)}
                                                error={Boolean(getHelperText('value', index) && getError('value', index))}
                                            >
                                                {facilities?.map(facility => (
                                                    <MenuItem
                                                        key={facility.id}
                                                        value={facility.id}
                                                    >
                                                        {facility.name}
                                                    </MenuItem>
                                                ))}

                                            </Select>
                                            <FormHelperText error>{getHelperText('value', index)}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    }
                                    {overrideCriteria.contractOverrideCriteriaId?.id === 6 &&
                                    <Grid item xs={6}>
                                        <FormControl className={classes.formControl}>
                                            <InputLabel htmlFor="role">Select Provider Role</InputLabel>
                                            <Select
                                                required
                                                value={overrideCriteria?.value}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                name={`contractOverrideGroupXref.${groupIndex}.contractOverrideCriteriaXref.${index}.value`}
                                                //helperText={getHelperText('npiRoleId', index) && getError('npiRoleId', index)}
                                                error={Boolean(getHelperText('value', index) && getError('value', index))}
                                            >
                                                {npiRoles?.map(role => (
                                                    <MenuItem
                                                        key={role.id}
                                                        value={role.id}
                                                    >
                                                        {role.name}
                                                    </MenuItem>
                                                ))}

                                            </Select>
                                            <FormHelperText error>{getHelperText('value', index)}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    }
                                    <Grid item xs={2}>
                                        <Grid container>
                                        {(index >= 0 && overrideGroup.contractOverrideCriteriaXref.length > 1) && (
                                            <IconButton
                                                edge="start"
                                                aria-label="Delete"
                                                onClick={() => remove(index)}
                                                className={classes.iconButton}
                                                size="large">
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                        {index === 0 && (
                                            <>
                                            <IconButton
                                                edge="start"
                                                color="primary"
                                                aria-label="Add"
                                                onClick={() => insert(overrideGroup.contractOverrideCriteriaXref.length, {
                                                    contractOverrideCriteriaId: null,
                                                    equator: '',
                                                    value: ''
                                                })}
                                                className={classes.iconButton}
                                                size="large">
                                                <AddCircleIcon />
                                            </IconButton>
                                            </>
                                        )}
                                        </Grid>
                                    </Grid>
                                    
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                )}
            />)
    );
}
